import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import debounce from 'lodash/debounce';
import { apiAgent } from '../../../axiosConfig.js';
import { InputBox } from './InputBox.jsx';
import { Loader } from '../Loader.jsx';
import { createLayerId, pushLayer, popLayer, getLayerZIndex } from '../Popup/popupStack.js';

/**
 * SuggestionBox — Autocomplete input with async API-driven suggestions.
 *
 * Composes InputBox for the text field and renders a floating suggestion
 * list below it. Fetches results from a GET endpoint with a debounce of 300ms.
 *
 * @param {string}   label          - Field label
 * @param {boolean}  showLabel      - Show the label
 * @param {string}   helperText     - Helper / error message below input
 * @param {boolean}  showHelperText - Show helper text
 * @param {string}   placeholder    - Input placeholder
 * @param {string}   Size           - "Small" | "Medium" | "Large"
 * @param {boolean}  disabled       - Disabled state
 * @param {string}   error          - Error message (triggers error styling)
 *
 * @param {string}   suggestionUrl  - GET endpoint; receives ?query=<text> + params
 * @param {object}   params         - Extra query params merged into every request
 * @param {number}   minChars       - Min characters before fetching (default 2)
 * @param {string}   name           - Passed as first arg to onSelect (mirrors old AnSuggestionBox API)
 * @param {func}     onSelect       - Called with (name, item) when an option is chosen
 * @param {func}     renderOption   - Optional: (item, isActive) => JSX — custom item renderer
 *
 * @param {boolean}  showIcon       - Show an icon inside the input field
 * @param {string}   IconName       - Icon name (from the Icons component)
 * @param {string}   IconClass      - CSS class for the icon wrapper (default "icon")
 * @param {object}   IconStyle      - Inline style overrides for the icon
 *
 * @param {string}   defaultValue   - Initial text shown in the input (e.g. current team name)
 * @param {string}   customClass    - Extra CSS class on the wrapper
 * @param {object}   customStyle    - Inline style on the wrapper
 */
export const SuggestionBox = ({
    label = '',
    showLabel = false,
    helperText = '',
    showHelperText = false,
    placeholder = 'Type to search...',
    Size = 'Medium',
    disabled = false,
    error = '',

    suggestionUrl,
    params = {},
    minChars = 2,
    name = '',
    onSelect,
    renderOption,

    showIcon = false,
    IconName = null,
    IconClass = 'icon',
    IconStyle = {},

    defaultValue = '',
    customClass = '',
    customStyle = {},
    usePortal = false,
    dropdownWidth,
    customMenuStyle = {},
}) => {
    const [inputText, setInputText] = useState(defaultValue);
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [menuStyle, setMenuStyle] = useState({});

    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const debounceRef = useRef(null);
    const layerIdRef = useRef(createLayerId());

    // Build debounced fetch — re-created only when URL or params change
    const debouncedFetch = useMemo(() => {
        const fn = debounce((query) => {
            setIsFetching(true);
            apiAgent({
                method: 'get',
                url: suggestionUrl,
                params: { query, ...params },
            })
                .then((res) => {
                    setSuggestions(res.data.data || []);
                    setIsFetching(false);
                    setIsOpen(true);
                    setActiveIndex(0);
                })
                .catch(() => {
                    setSuggestions([]);
                    setIsFetching(false);
                    setIsOpen(true);
                });
        }, 300);
        debounceRef.current = fn;
        return fn;
    // params is an object — JSON.stringify ensures the effect re-runs when its contents change, not just its reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestionUrl, JSON.stringify(params)]);

    // Cancel any pending debounced call on unmount
    useEffect(() => {
        return () => {
            debounceRef.current?.cancel();
        };
    }, []);

    // Close on outside click
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (
                containerRef.current && !containerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleReset = useCallback(() => {
        setSuggestions([]);
        setIsOpen(false);
        setIsFetching(false);
        setActiveIndex(0);
    }, []);

    const handleChange = useCallback((e) => {
        const value = e.target.value;
        setInputText(value);
        if (!value) {
            debounceRef.current?.cancel();
            handleReset();
            onSelect?.(name, null);
            return;
        }
        if (value.length >= minChars) {
            debouncedFetch(value);
        }
    }, [debouncedFetch, handleReset, minChars, name, onSelect]);

    const handleSelect = useCallback((item) => {
        setInputText(item.name);
        setSuggestions([]);
        setIsOpen(false);
        setActiveIndex(0);
        onSelect?.(name, item);
    }, [name, onSelect]);

    const handleKeyDown = useCallback((e) => {
        if (!isOpen && !isFetching) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions[activeIndex]) {
                handleSelect(suggestions[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            handleReset();
        }
    }, [isOpen, isFetching, suggestions, activeIndex, handleSelect, handleReset]);

    const showMenu = (isOpen || isFetching) && !!inputText;

    // Track input position for portal rendering.
    // Registers with popupStack so the menu always stacks above any open popup.
    useEffect(() => {
        if (!usePortal || !showMenu) return;
        const layerId = layerIdRef.current;
        pushLayer(layerId);
        const zIndex = getLayerZIndex(layerId);
        const update = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                right: 'auto',
                marginTop: 0,
                zIndex,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            popLayer(layerId);
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [usePortal, showMenu]);

    return (
        <div className={`suggestion-box ${customClass}`} ref={containerRef} style={customStyle}>
            <InputBox
                value={inputText}
                handleOnChange={handleChange}
                handleOnKeyDown={handleKeyDown}
                label={label}
                showLabel={showLabel}
                helperText={helperText}
                showHelperText={showHelperText}
                placeholder={placeholder}
                Size={Size}
                disabled={disabled}
                error={error}
                showIcon={showIcon}
                IconName={IconName}
                IconClass={IconClass}
                IconStyle={IconStyle}
            />
            {showMenu && (() => {
                const menu = (
                    <ul className="suggestion-box__menu" ref={menuRef} style={{ ...(usePortal ? menuStyle : {}), ...(dropdownWidth ? { width: dropdownWidth } : {}), ...customMenuStyle }}>
                        {isFetching && (
                            <li className="suggestion-box__loader">
                                <Loader isLoading={true} Size="Small" />
                            </li>
                        )}
                        {!isFetching && suggestions.length === 0 && (
                            <li className="suggestion-box__item body-m suggestion-box__item--no-results">
                                No results
                            </li>
                        )}
                        {!isFetching && suggestions.map((item, i) => (
                            <li
                                key={item.id}
                                className={`suggestion-box__item body-m${i === activeIndex ? ' suggestion-box__item--active' : ''}`}
                                onMouseDown={(e) => e.preventDefault()} // prevent input blur before click
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setActiveIndex(i)}
                            >
                                {renderOption ? renderOption(item, i === activeIndex) : item.name}
                            </li>
                        ))}
                    </ul>
                );
                return usePortal ? ReactDOM.createPortal(menu, document.body) : menu;
            })()}
        </div>
    );
};

export default SuggestionBox;
